import React, { useEffect, useState } from "react";
import { Loader2, Plus, Mail, Eye, MousePointerClick, IdCard, Send, Users, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const MSTAT = [
    { key: "prospects", Icon: Users, label: "Prospects" },
    { key: "sent", Icon: Send, label: "Sent" },
    { key: "opens", Icon: Eye, label: "Opens" },
    { key: "clicks", Icon: MousePointerClick, label: "Clicks" },
    { key: "passports_started", Icon: IdCard, label: "Passports" },
    { key: "verified", Icon: TrendingUp, label: "Verified" },
];

export default function RecruitOutreach() {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: "", vertical: "", description: "" });
    const [creating, setCreating] = useState(false);

    const load = () => api.get("/admin/recruit/campaigns")
        .then((r) => setCampaigns(r.data.items || []))
        .catch((err) => toast.error(formatApiError(err)))
        .finally(() => setLoading(false));

    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.name.trim()) return toast.error("Name is required.");
        setCreating(true);
        try {
            await api.post("/admin/recruit/campaigns", form);
            setForm({ name: "", vertical: "", description: "" });
            setShowForm(false);
            toast.success("Campaign created.");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setCreating(false); }
    };

    return (
        <div data-testid="recruit-outreach" className="space-y-4">
            <div className="card-soft p-4 flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-heading font-medium text-lg">Outreach Campaigns</h2>
                    <p className="text-sm text-muted-foreground">Track prospects → sent → opens → clicks → passports for each vertical.</p>
                </div>
                <Button onClick={() => setShowForm(true)} data-testid="outreach-create-campaign"><Plus size={13} className="mr-2" /> New campaign</Button>
            </div>

            {showForm && (
                <div className="card-soft p-4 space-y-3" data-testid="outreach-create-form">
                    <Input placeholder="Campaign name (e.g. Hospitality Hiring Q1)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="outreach-form-name" />
                    <Input placeholder="Vertical (hospitality, dental, accounting, …)" value={form.vertical} onChange={(e) => setForm({ ...form, vertical: e.target.value })} data-testid="outreach-form-vertical" />
                    <Textarea rows={2} placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="outreach-form-description" />
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
                        <Button onClick={create} disabled={creating} data-testid="outreach-form-save">
                            {creating ? <Loader2 size={13} className="animate-spin mr-2" /> : null}
                            Create
                        </Button>
                    </div>
                </div>
            )}

            {loading ? <div className="py-12 flex justify-center"><Loader2 size={20} className="animate-spin text-violet-500" /></div>
                : campaigns.length === 0 ? (
                    <div className="card-soft p-10 text-center">
                        <Mail size={22} className="mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground mt-2">No campaigns yet — create your first one to start tracking outreach.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="outreach-campaigns-grid">
                        {campaigns.map((c) => (
                            <div key={c.id} className="card-soft p-4" data-testid={`campaign-card-${c.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="font-heading font-medium truncate">{c.name}</div>
                                        <div className="text-xs text-muted-foreground truncate">{c.vertical || "General"} · created {new Date(c.created_at).toLocaleDateString()}</div>
                                    </div>
                                </div>
                                <div className="mt-3 grid grid-cols-3 gap-2">
                                    {MSTAT.map((s) => (
                                        <div key={s.key} className="rounded-md bg-muted/40 p-2 text-center">
                                            <div className="flex items-center justify-center text-muted-foreground"><s.Icon size={11} /></div>
                                            <div className="font-heading font-bold text-lg mono-score">{c.metrics?.[s.key] ?? 0}</div>
                                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
        </div>
    );
}
